import React from 'react';
import { useSelector } from 'react-redux';
import { Button, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useScreenResolution } from '@app/hooks';
import { selectHasFiber } from '@app/redux';
import { selectChangePlan, selectChosenPlan, selectFiberDowngradeFee } from '@app/redux/changePlan';

import { useConsentCheck } from '../../../../hooks/use-consent-check';
import Contract from '../Contract';
import Disclosure from '../Disclosure';
import EquipmentLeaseFee from '../EquipmentLeaseFee';
import EquipmentShipping from '../EquipmentShipping';
import { FiberOneTimeFee } from '../FiberOneTimeFee';
import Notice from '../Notice';
import { OptionalServices } from '../OptionalServices';
import { useHasCarryOverAddons } from '../OptionalServices/useHasCarryOverAddons';
import { ModalTypes } from '../../../../types';

import styles from './styles.module.scss';

type OrderDetailsCardProps = {
  handlePlacePlanOrder: () => void;
  openModal: (e: React.MouseEvent<HTMLElement>, modalType: ModalTypes) => void;
  closeModal: () => void;
  isTermsAndConditionsModalOpen: boolean;
  isCalculationModalOpen: boolean;
  hasAttemptedSubmit?: boolean;
};

const OrderDetailsCard = ({
  openModal,
  closeModal,
  handlePlacePlanOrder,
  isTermsAndConditionsModalOpen,
  hasAttemptedSubmit,
}: OrderDetailsCardProps) => {
  const { t } = useTranslation('ChangePlan');

  const hasFiber = useSelector(selectHasFiber);
  const fiberDowngradeFee = useSelector(selectFiberDowngradeFee);

  const { priceCurrency } = useSelector(selectChosenPlan);
  const {
    cart: { hasEquipmentForSelfInstall, eicRequired },
    isEicChecked,
    requiresEquipmentChange,
  } = useSelector(selectChangePlan);

  const { hasCarryOverAddons, carryOverAddons } = useHasCarryOverAddons();

  const { checkConsent } = useConsentCheck(isEicChecked, eicRequired);

  const { isSmall, isExtraSmall, isMedium } = useScreenResolution();
  const isMobile = isSmall || isExtraSmall;
  const isMobileOrTablet = isMobile || isMedium;

  const handleClick = () => {
    if (!checkConsent()) {
      return;
    }
    handlePlacePlanOrder();
  };

  return (
    <Surface
      variant="primary"
      radius={isMobile ? '0px' : '16px'}
      className={styles['order-review-details']}
      data-cy="order-review-details"
    >
      <div className={styles['details-content']}>
        <Txt variant="bodyLargeBold">{t('orderReview.details.title')}</Txt>
        {requiresEquipmentChange && <EquipmentLeaseFee priceCurrency={priceCurrency || ''} />}
        <Contract />
        {hasFiber && fiberDowngradeFee && <FiberOneTimeFee />}
        {hasEquipmentForSelfInstall && <EquipmentShipping />}
        {hasCarryOverAddons && <OptionalServices carryOverAddons={carryOverAddons} />}
        {eicRequired && (
          <Disclosure isEicChecked={isEicChecked ?? false} hasAttemptedSubmit={hasAttemptedSubmit ?? false} />
        )}
        <Notice openModal={openModal} closeModal={closeModal} isModalOpen={isTermsAndConditionsModalOpen} />
      </div>
      {(requiresEquipmentChange || !isMobileOrTablet) && (
        <div className={styles['details-footer']}>
          <Button
            data-cy="submit-order-button"
            onClick={handleClick}
            className={isMobileOrTablet ? styles['mobile-or-tablet-button'] : undefined}
          >
            {requiresEquipmentChange
              ? t('orderReview.cartDetails.next')
              : t('orderReview.cartDetails.placeOrderButton')}
          </Button>
        </div>
      )}
    </Surface>
  );
};

export default OrderDetailsCard;
